import renderAdminTrendChart from './AdminTrendChart.js';

const COLOR_GUEST = '#a78bfa';
const COLOR_REGISTERED = '#526dff';
const COLOR_CONVERSION = '#c7ff45';
const COLOR_CONNECTIONS = '#f472b6';
const COLOR_SESSIONS = '#94a3b8';
const COLOR_PLAYERS = '#fb923c';

function statCard(label, value) {
  return `<div class="admin-stat-card"><span class="admin-stat-value">${value}</span><span class="admin-stat-label">${label}</span></div>`;
}

function fmtPct(v) {
  return v == null ? '—' : `${v}%`;
}

function fmtParisTime(iso) {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export default class AdminStatsPanel {
  constructor({ api, root }) {
    this.api = api;
    this.root = root;
    this.period = 30;
    this.stats = null;
    this.trends = null;

    this.root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-period]');
      if (btn) {
        this.period = Number(btn.dataset.period);
        this._renderPeriodTabs();
        this._loadTrends();
      }
    });
  }

  async load() {
    this.root.innerHTML = '<p class="admin-msg">Chargement…</p>';
    const res = await this.api.get('/admin/stats');
    if (!res.ok) {
      this.root.innerHTML = '<p class="admin-msg admin-msg-error">Impossible de charger les stats.</p>';
      return;
    }
    this.stats = res.data;
    this._render();
    this._loadTrends();
  }

  async _loadTrends() {
    const chartBlocks = this.root.querySelectorAll('[data-chart]');
    chartBlocks.forEach((el) => { el.innerHTML = '<p class="admin-msg">Chargement…</p>'; });

    const res = await this.api.get(`/admin/stats/trends?days=${this.period}`);
    if (!res.ok) return;
    this.trends = res.data;
    this._renderCharts();
  }

  _renderPeriodTabs() {
    const tabs = this.root.querySelector('.admin-period-tabs');
    if (!tabs) return;
    tabs.querySelectorAll('[data-period]').forEach((btn) => {
      btn.classList.toggle('active', Number(btn.dataset.period) === this.period);
      btn.setAttribute('aria-selected', String(Number(btn.dataset.period) === this.period));
    });
  }

  _renderCharts() {
    if (!this.trends?.series) return;

    const signupEl = this.root.querySelector('[data-chart="signup"]');
    if (signupEl) {
      renderAdminTrendChart(signupEl, {
        series: this.trends.series,
        metrics: [
          { key: 'newGuestAccounts', label: 'Invités', color: COLOR_GUEST },
          { key: 'newRegisteredUsers', label: 'Comptes validés', color: COLOR_REGISTERED },
        ],
      });
    }

    const convEl = this.root.querySelector('[data-chart="conversions"]');
    if (convEl) {
      renderAdminTrendChart(convEl, {
        series: this.trends.series,
        metrics: [
          { key: 'guestConversions', label: 'Conversions', color: COLOR_CONVERSION },
        ],
      });
    }

    const connEl = this.root.querySelector('[data-chart="connections"]');
    if (connEl) {
      renderAdminTrendChart(connEl, {
        series: this.trends.series,
        metrics: [
          { key: 'connections', label: 'Connexions', color: COLOR_CONNECTIONS },
        ],
      });
    }

    const activityEl = this.root.querySelector('[data-chart="activity"]');
    if (activityEl) {
      renderAdminTrendChart(activityEl, {
        series: this.trends.series,
        metrics: [
          { key: 'sessionsStarted', label: 'Parties démarrées', color: COLOR_SESSIONS },
          { key: 'activePlayers', label: 'Joueurs actifs', color: COLOR_PLAYERS, type: 'line' },
        ],
      });
    }
  }

  _render() {
    const s = this.stats;
    const guest = s.guestStats ?? {};
    const conn = s.connectionStats ?? {};
    const detail = s.sessionsTodayDetail;
    const summary = detail?.summary ?? {};

    const topSongs = (s.topSongs30d ?? [])
      .map((row) => `<li><span>${esc(row.songId)}</span><strong>${row.plays}</strong></li>`)
      .join('') || '<li class="admin-muted">Aucune partie</li>';

    const sessionRows = (detail?.sessions ?? []).map((sess) => `
      <tr>
        <td>${fmtParisTime(sess.startedAt)}</td>
        <td>${esc(sess.username)}</td>
        <td><span class="admin-badge${sess.isGuest ? '' : ' admin-badge-muted'}">${sess.isGuest ? 'Invité' : 'Validé'}</span></td>
        <td>${esc(sess.songId)}</td>
        <td>${esc(sess.difficulty)}</td>
        <td><span class="admin-badge${sess.completed ? ' admin-badge-ok' : ' admin-badge-muted'}">${sess.completed ? 'Terminée' : 'Abandonnée'}</span></td>
        <td>${sess.completed ? (sess.finishScore ?? '—') : '—'}</td>
      </tr>
    `).join('');

    this.root.innerHTML = `
      <div class="admin-stats">
        <section class="admin-panel-section">
          <h2>Utilisateurs</h2>
          <div class="admin-stat-grid">
            ${statCard('Comptes validés', s.totalRegisteredUsers)}
            ${statCard('Nouveaux 7j', s.newUsers7d)}
            ${statCard('Nouveaux 30j', s.newUsers30d)}
          </div>
        </section>

        <section class="admin-panel-section">
          <h2>Comptes invités</h2>
          <div class="admin-stat-grid">
            ${statCard('Invités actifs', guest.activeGuestAccounts)}
            ${statCard('Total invités', guest.totalGuestAccounts)}
            ${statCard('Nouveaux invités 7j', guest.newGuestAccounts7d)}
            ${statCard('Nouveaux invités 30j', guest.newGuestAccounts30d)}
            ${statCard('Parties invités aujourd\'hui', guest.guestSessionsToday)}
            ${statCard('Joueurs invités actifs', guest.activeGuestPlayersToday)}
            ${statCard('Conversions 7j', guest.guestConversions7d)}
            ${statCard('Conversions 30j', guest.guestConversions30d)}
          </div>
        </section>

        <section class="admin-panel-section">
          <h2>Connexions</h2>
          <div class="admin-stat-grid">
            ${statCard('Aujourd\'hui', conn.connectionsToday)}
            ${statCard('7 jours', conn.connections7d)}
            ${statCard('30 jours', conn.connections30d)}
            ${statCard('Logins aujourd\'hui', conn.loginsToday)}
            ${statCard('Logins 7j', conn.logins7d)}
            ${statCard('Restaurations invité', conn.guestRestoresToday)}
            ${statCard('Utilisateurs actifs (DAU)', conn.dau)}
          </div>
        </section>

        <section class="admin-panel-section">
          <h2>Parties jouées</h2>
          <div class="admin-stat-grid">
            ${statCard('Démarrées aujourd\'hui', s.sessionsToday)}
            ${statCard('Démarrées 7j', s.sessions7d)}
            ${statCard('Démarrées 30j', s.sessions30d)}
            ${statCard('Terminées aujourd\'hui', s.finishedToday)}
            ${statCard('Terminées 7j', s.finished7d)}
            ${statCard('Taux complétion 30j', fmtPct(s.completionRatePct))}
            ${statCard('Joueurs actifs aujourd\'hui', s.activePlayersToday)}
          </div>
        </section>

        <section class="admin-panel-section">
          <h2>Top chansons (30j)</h2>
          <ul class="admin-top-list">${topSongs}</ul>
        </section>

        ${detail ? `
        <section class="admin-panel-section">
          <h2>Parties du jour (${esc(detail.date)})</h2>
          <div class="admin-stat-grid">
            ${statCard('Total', summary.total)}
            ${statCard('Terminées', summary.finished)}
            ${statCard('Abandonnées', summary.abandoned)}
            ${statCard('Invités', summary.guest)}
            ${statCard('Validés', summary.registered)}
          </div>
          ${detail.truncated ? `<p class="admin-chart-note">Affichage limité à 200 sur ${summary.total} parties.</p>` : ''}
          ${detail.sessions?.length ? `
          <div class="admin-table-scroll">
            <table class="admin-table admin-table-compact">
              <thead>
                <tr>
                  <th>Heure</th><th>Joueur</th><th>Type</th><th>Chanson</th><th>Diff.</th><th>Statut</th><th>Score</th>
                </tr>
              </thead>
              <tbody>${sessionRows}</tbody>
            </table>
          </div>` : '<p class="admin-msg">Aucune partie aujourd\'hui.</p>'}
        </section>` : ''}

        <section class="admin-panel-section admin-trends">
          <div class="admin-trends-head">
            <h2>Tendances</h2>
            <div class="admin-period-tabs" role="tablist">
              ${[7, 30, 90].map((d) => `
                <button type="button" class="admin-tab admin-tab--sm${this.period === d ? ' active' : ''}" data-period="${d}" role="tab" aria-selected="${this.period === d}">${d}j</button>
              `).join('')}
            </div>
          </div>
          <div class="admin-chart-block"><h3>Inscriptions</h3><div data-chart="signup"></div></div>
          <div class="admin-chart-block"><h3>Conversions</h3><div data-chart="conversions"></div></div>
          <div class="admin-chart-block"><h3>Connexions</h3><div data-chart="connections"></div></div>
          <div class="admin-chart-block"><h3>Activité</h3><div data-chart="activity"></div></div>
        </section>
      </div>
    `;
  }
}
